export interface QueryCacheEntry<TValue> {
    value: TValue;
    cachedAt: number;
}

export interface QueryCache<TValue> {
    get(key: string): QueryCacheEntry<TValue> | undefined;
    set(key: string, entry: QueryCacheEntry<TValue>): void;
    setValue(key: string, value: TValue): void;
    delete(key: string): void;
    clear(): void;
}

export const createQueryCache = <TValue>(ttlMs: number): QueryCache<TValue> => {
    const cache = new Map<string, QueryCacheEntry<TValue>>();

    const isFresh = (entry: QueryCacheEntry<TValue>): boolean => {
        return Date.now() - entry.cachedAt < ttlMs;
    };

    return {
        get(key: string): QueryCacheEntry<TValue> | undefined {
            const entry = cache.get(key);
            if (!entry) {
                return undefined;
            }
            if (!isFresh(entry)) {
                cache.delete(key);
                return undefined;
            }
            return entry;
        },

        set(key: string, entry: QueryCacheEntry<TValue>): void {
            cache.set(key, entry);
        },

        setValue(key: string, value: TValue): void {
            cache.set(key, { value, cachedAt: Date.now() });
        },

        delete(key: string): void {
            cache.delete(key);
        },

        clear(): void {
            cache.clear();
        },
    };
};

type Primitive = string | number | boolean | Date | null | undefined;

const normalizeValue = (value: unknown): unknown => {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (typeof value !== 'object') {
        return value as Primitive;
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (Array.isArray(value)) {
        const normalizedArray = value
            .map((item) => normalizeValue(item))
            .filter((item) => item !== undefined);
        return normalizedArray.length > 0 ? normalizedArray : undefined;
    }

    const entries = Object.keys(value as Record<string, unknown>)
        .sort()
        .map((key) => {
            const normalized = normalizeValue((value as Record<string, unknown>)[key]);
            return normalized === undefined ? undefined : ([key, normalized] as [string, unknown]);
        })
        .filter((item): item is [string, unknown] => item !== undefined);

    if (entries.length === 0) {
        return undefined;
    }

    return entries.reduce<Record<string, unknown>>((acc, [key, normalized]) => {
        acc[key] = normalized;
        return acc;
    }, {});
};

export const serializeQuery = (query: Record<string, unknown>): string => {
    const normalized = normalizeValue(query);
    return JSON.stringify(normalized ?? {});
};
